import { Card, CardHeader } from '../../../components/Card.jsx'
import { Tile, PrevalenceList, Suppressed, MethodNote, fmtNum, fmtPct } from '../ui.jsx'

/**
 * National Workforce Health — the population health of organisation-linked
 * staff pooled across every participating employer. Chronic prevalence +
 * measured risk for the workforce cohort specifically (vs the whole-
 * population Risk Factors / Chronic tabs).
 */
export default function WorkforceHealth({ data }) {
  const wf = data.workforce_health || {}
  const mr = wf.measured_risk || {}
  const measuredN = mr.measured_n || 0

  const riskBands = [
    { key: 'raised_bp', label: 'Raised BP', colour: 'bg-amber-500' },
    { key: 'hypertensive', label: 'Hypertensive', colour: 'bg-rose-500' },
    { key: 'overweight_or_obese', label: 'Overweight / obese', colour: 'bg-amber-500' },
    { key: 'obese', label: 'Obese', colour: 'bg-rose-500' },
    { key: 'raised_bsl', label: 'Raised blood glucose', colour: 'bg-rose-500' },
  ]

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="National workforce health"
          subtitle="Pooled across every participating employer — companies, government agencies, NGOs, institutions and unions. Aggregate-only, k=5 privacy floor."
          right={
            <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
              employer cohort
            </span>
          }
        />
        <MethodNote>
          Organisation-linked staff pooled across all employers. Chronic
          prevalence uses confirmed diagnoses; measured risk uses each person's
          latest vitals. Buckets below 5 are hidden (k=5 floor).
        </MethodNote>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="Participating employers" value={fmtNum(wf.participating_employers)} colour="sky" />
          <Tile label="Staff enrolled" value={fmtNum(wf.staff_enrolled)} colour="wantok" />
          <Tile label="Staff screened" value={fmtNum(wf.staff_screened)} colour="emerald" />
          <Tile label="Staff measured" value={fmtNum(measuredN)} colour="slate" />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader
            title="Workforce chronic prevalence"
            subtitle="Distinct affected staff per condition, across all employers."
          />
          <PrevalenceList
            items={wf.chronic_prevalence}
            colour="emerald"
            denominatorLabel={`Percentages are of ${fmtNum(wf.staff_enrolled)} enrolled staff.`}
          />
        </Card>

        <Card>
          <CardHeader
            title="Workforce measured risk"
            subtitle="Cardiometabolic risk bands from staff's latest recorded vitals."
          />
          <ul className="space-y-2.5">
            {riskBands.map((b) => {
              const v = mr[b.key] || {}
              if (v.suppressed || v.count == null) {
                return (
                  <li key={b.key} className="text-sm flex items-center justify-between gap-2">
                    <span className="text-slate-500">{b.label}</span>
                    <Suppressed />
                  </li>
                )
              }
              const pct = v.pct_of_measured || 0
              return (
                <li key={b.key} className="text-sm">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-slate-700">{b.label}</span>
                    <span className="font-semibold tabular-nums text-slate-900">
                      {fmtNum(v.count)}
                      <span className="text-[11px] text-slate-500 ml-2">{fmtPct(pct)}</span>
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className={`h-full rounded-full ${b.colour}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      </div>
    </div>
  )
}
